import { useEffect, useRef, useState } from "react";
import {
  API_BASE_URL,
  browserPublicShareFetch,
} from "./api-base";
import {
  SHARE_RESULT_MAX_RESPONSE_BYTES,
  parsePublicSharedResult,
  parseSharedResultBootstrap,
  readStoredSharedResultLocale,
  resolveSharedResultLocale,
  writeStoredSharedResultLocale,
  type PublicSharedResult,
  type SharedEvidenceState,
  type SharedResultLoadState,
  type SharedResultLocale,
} from "../shared-result-contract";
import {
  sharedResultMetadata,
  sharedResultText,
} from "../shared-result-i18n";
import { createTranslator, isRTL } from "./i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { researchT } from "./research-i18n";
import {
  formatLocalizedDateTime,
  formatLocalizedNumber,
  formatLocalizedPercent,
} from "./locale-format";
import {
  blockSharedResult,
  reportSharedResult,
  sharedResearchActionAvailability,
  sharedResultReportSupportLink,
  sharedResultIsBlocked,
  sharedStrategyDestinationWithBestEffortAttribution,
  type SharedResultReportReceipt,
} from "./shared-research-network";
import "./shared-result.css";

const SHARE_ORIGIN = "https://share.backtest.click";
const APP_STORE_URL = "https://apps.apple.com/app/id6783314365";

type PageState = SharedResultLoadState | { kind: "loading" };

function bootstrapState(): SharedResultLoadState | null {
  const element = document.getElementById("shared-result-bootstrap");
  if (!element?.textContent) return null;
  try {
    return parseSharedResultBootstrap(JSON.parse(element.textContent) as unknown)?.state || null;
  } catch {
    return null;
  }
}

async function readBoundedJSON(response: Response): Promise<unknown> {
  const declaredLength = Number(response.headers.get("content-length"));
  if (Number.isFinite(declaredLength) && declaredLength > SHARE_RESULT_MAX_RESPONSE_BYTES) {
    throw new Error("response_too_large");
  }
  if (!response.body) throw new Error("empty_response");
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  while (true) {
    const item = await reader.read();
    if (item.done) break;
    size += item.value.byteLength;
    if (size > SHARE_RESULT_MAX_RESPONSE_BYTES) {
      await reader.cancel("response_too_large");
      throw new Error("response_too_large");
    }
    chunks.push(item.value);
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
}

async function fetchSharedResult(token: string, signal: AbortSignal): Promise<SharedResultLoadState> {
  try {
    const response = await browserPublicShareFetch(
      `${API_BASE_URL}/api/v2/shareable-results/public`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Share-Token": token,
        },
        credentials: "omit",
        cache: "no-store",
        redirect: "error",
        referrerPolicy: "no-referrer",
        signal,
      },
    );
    if (response.status === 404) return { kind: "not_found" };
    if (response.status === 410) return { kind: "expired" };
    if (response.status === 503) return { kind: "service_unavailable" };
    if (!response.ok || !response.headers.get("content-type")?.toLowerCase().includes("application/json")) {
      return { kind: "service_unavailable" };
    }
    const result = parsePublicSharedResult(await readBoundedJSON(response));
    return result ? { kind: "ready", result } : { kind: "service_unavailable" };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return { kind: "service_unavailable" };
  }
}

function upsertMeta(attribute: "name" | "property", key: string, value: string) {
  const selector = `meta[${attribute}="${key}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = value;
}

function protectBearerURL(locale: SharedResultLocale, token: string, result?: PublicSharedResult) {
  document.documentElement.lang = locale;
  document.documentElement.dir = isRTL(locale) ? "rtl" : "ltr";
  document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((item) => item.remove());
  const metadata = sharedResultMetadata(locale, result?.symbol, result?.timeframe);
  document.title = metadata.title;
  upsertMeta("name", "robots", "noindex,nofollow,noarchive");
  upsertMeta("name", "referrer", "no-referrer");
  upsertMeta("name", "description", metadata.description);
  upsertMeta("name", "apple-itunes-app", `app-id=6783314365, app-argument=${SHARE_ORIGIN}/r/${token}`);
  upsertMeta("property", "og:title", metadata.title);
  upsertMeta("property", "og:description", metadata.description);
  upsertMeta("name", "twitter:title", metadata.title);
  upsertMeta("name", "twitter:description", metadata.description);
}

function initialLocale() {
  const stored = readStoredSharedResultLocale(() => window.localStorage);
  return resolveSharedResultLocale(stored || navigator.languages?.join(",") || navigator.language || "en");
}

function dateOnly(value: string | null, locale: SharedResultLocale): string {
  if (!value) return "—";
  return formatLocalizedDateTime(value, locale, { year: "numeric", month: "short", day: "2-digit" });
}

function dateTime(value: string | null, locale: SharedResultLocale): string {
  if (!value) return "—";
  return formatLocalizedDateTime(value, locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function evidenceLabel(state: SharedEvidenceState, locale: SharedResultLocale): string {
  switch (state) {
    case "in_sample": return researchT(locale, "evidenceInSample");
    case "selection_passed": return researchT(locale, "evidenceSelection");
    case "locked_holdout_passed": return researchT(locale, "evidenceHoldoutPass");
    case "locked_holdout_failed": return researchT(locale, "evidenceHoldoutFail");
    case "insufficient_evidence": return researchT(locale, "evidenceInsufficient");
    case "unvalidated":
    case "unknown":
      return researchT(locale, "evidenceNotEvaluated");
  }
}

function evidenceTone(state: SharedEvidenceState): string {
  if (state === "locked_holdout_passed" || state === "selection_passed") return "positive";
  if (state === "locked_holdout_failed" || state === "insufficient_evidence") return "caution";
  return "neutral";
}

function EquityCurve({ result, locale }: { result: PublicSharedResult; locale: SharedResultLocale }) {
  const points = result.equityCurve;
  const width = 960;
  const height = 300;
  const inset = 22;
  const values = points.map((point) => point.returnPercent);
  const minimum = Math.min(0, ...values);
  const maximum = Math.max(0, ...values);
  const spread = Math.max(maximum - minimum, 1);
  const x = (progress: number) => inset + progress * (width - inset * 2);
  const y = (value: number) => inset + (maximum - value) / spread * (height - inset * 2);
  const line = points.map((point, index) => `${index ? "L" : "M"}${x(point.progress).toFixed(2)},${y(point.returnPercent).toFixed(2)}`).join(" ");
  const finalPoint = points[points.length - 1];
  const area = points.length
    ? `${line} L${x(finalPoint?.progress || 1).toFixed(2)},${(height - inset).toFixed(2)} L${x(points[0]?.progress || 0).toFixed(2)},${(height - inset).toFixed(2)} Z`
    : "";
  const finalReturn = finalPoint?.returnPercent;
  const label = `${sharedResultText(locale, "equityCurve")}. ${sharedResultText(locale, "equityDescription")}`;

  if (points.length < 2) {
    return <div className="shared-equity-empty">{createTranslator(locale)("notAvailable")}</div>;
  }

  return (
    <svg className="shared-equity-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <title>{label}</title>
      <defs>
        <linearGradient id="shared-equity-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={finalReturn != null && finalReturn < 0 ? "#dc2626" : "#2563eb"} stopOpacity="0.24" />
          <stop offset="1" stopColor={finalReturn != null && finalReturn < 0 ? "#dc2626" : "#2563eb"} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <line className="shared-equity-zero" x1={inset} x2={width - inset} y1={y(0)} y2={y(0)} />
      <path d={area} fill="url(#shared-equity-fill)" />
      <path className={finalReturn != null && finalReturn < 0 ? "is-negative" : ""} d={line} fill="none" />
    </svg>
  );
}

function LoadingState({ locale }: { locale: SharedResultLocale }) {
  return (
    <main className="shared-state-card" aria-live="polite" aria-busy="true">
      <span className="shared-loading-indicator" aria-hidden="true" />
      <h1>{sharedResultText(locale, "loadingTitle")}</h1>
      <p>{sharedResultText(locale, "loadingBody")}</p>
    </main>
  );
}

function EmptyState({ state, locale }: { state: Exclude<SharedResultLoadState, { kind: "ready" }>; locale: SharedResultLocale }) {
  const titleKey = state.kind === "expired"
    ? "expiredTitle"
    : state.kind === "service_unavailable"
      ? "unavailableTitle"
      : "notFoundTitle";
  const bodyKey = state.kind === "expired"
    ? "expiredBody"
    : state.kind === "service_unavailable"
      ? "unavailableBody"
      : "notFoundBody";
  const prefix = locale === "en" ? "" : `/${locale}`;
  return (
    <main className="shared-state-card" role={state.kind === "service_unavailable" ? "alert" : undefined}>
      <div className={`shared-state-symbol is-${state.kind}`} aria-hidden="true">{state.kind === "expired" ? "⌛" : state.kind === "service_unavailable" ? "↻" : "×"}</div>
      <h1>{sharedResultText(locale, titleKey)}</h1>
      <p>{sharedResultText(locale, bodyKey)}</p>
      <div className="shared-state-actions">
        {state.kind === "service_unavailable" && (
          <button type="button" onClick={() => window.location.reload()}>{sharedResultText(locale, "retry")}</button>
        )}
        <a href={`https://backtest.click${prefix}/`}>{sharedResultText(locale, "homepage")}</a>
        <a href={`https://backtest.click${prefix}/backtest`}>{sharedResultText(locale, "newBacktest")}</a>
      </div>
    </main>
  );
}

function EvidenceReport({
  result,
  locale,
  duplicateBusy,
  duplicateError,
  onDuplicate,
  onReport,
  onBlock,
}: {
  result: PublicSharedResult;
  locale: SharedResultLocale;
  duplicateBusy: boolean;
  duplicateError: boolean;
  onDuplicate(): void;
  onReport(): void;
  onBlock(): void;
}) {
  const t = createTranslator(locale);
  const prefix = locale === "en" ? "" : `/${locale}`;
  const networkActions = sharedResearchActionAvailability(result);
  const metrics = [
    { label: t("returnMetric"), value: result.metrics.totalReturnPercent == null ? "—" : formatLocalizedPercent(result.metrics.totalReturnPercent, locale, 2, "always"), tone: (result.metrics.totalReturnPercent || 0) >= 0 ? "profit" : "loss" },
    { label: t("drawdown"), value: result.metrics.maxDrawdownPercent == null ? "—" : formatLocalizedPercent(result.metrics.maxDrawdownPercent, locale, 2), tone: "" },
    { label: t("winRate"), value: result.metrics.winRatePercent == null ? "—" : formatLocalizedPercent(result.metrics.winRatePercent, locale, 2), tone: "" },
    { label: researchT(locale, "metricProfitFactor"), value: result.metrics.profitFactor == null ? "—" : formatLocalizedNumber(result.metrics.profitFactor, locale, { maximumFractionDigits: 2 }), tone: "" },
    { label: t("trades"), value: formatLocalizedNumber(result.metrics.totalTrades, locale), tone: "" },
  ];

  return (
    <main className="shared-report" id="main-content">
      <section className="shared-report-hero" aria-labelledby="shared-result-title">
        <div className="shared-report-kickers">
          <span>{sharedResultText(locale, "readOnly")}</span>
          <span className={evidenceTone(result.evidenceState)}>{evidenceLabel(result.evidenceState, locale)}</span>
        </div>
        <p className="shared-report-eyebrow">{sharedResultText(locale, "sharedTitle")}</p>
        <h1 id="shared-result-title">{result.symbol} <span>· {result.timeframe}</span></h1>
        <p className="shared-strategy-label">{result.strategyLabel}</p>
        <p className="shared-summary-only">
          {sharedResultText(locale, result.reproduction ? "reproducible" : "summaryOnly")}
        </p>
        <dl className="shared-report-period">
          <div><dt>{sharedResultText(locale, "testedPeriod")}</dt><dd>{dateOnly(result.testedPeriod.start, locale)} – {dateOnly(result.testedPeriod.end, locale)}</dd></div>
          <div><dt>{sharedResultText(locale, "createdAt")}</dt><dd>{dateTime(result.createdAt, locale)}</dd></div>
          {result.expiresAt && <div><dt>{sharedResultText(locale, "expiresAt")}</dt><dd>{dateTime(result.expiresAt, locale)}</dd></div>}
        </dl>
      </section>

      <section className="shared-report-panel" aria-labelledby="shared-metrics-title">
        <div className="shared-section-heading">
          <div><p>{t("researchEvidence")}</p><h2 id="shared-metrics-title">{result.symbol} · {result.timeframe}</h2></div>
          <span className="shared-evidence-pill">{evidenceLabel(result.evidenceState, locale)}</span>
        </div>
        <dl className="shared-metric-grid">
          {metrics.map((metric) => (
            <div key={metric.label}><dt>{metric.label}</dt><dd className={metric.tone}>{metric.value}</dd></div>
          ))}
        </dl>
      </section>

      <section className="shared-report-panel" aria-labelledby="shared-equity-title">
        <div className="shared-section-heading">
          <div><p>{t("results")}</p><h2 id="shared-equity-title">{sharedResultText(locale, "equityCurve")}</h2></div>
        </div>
        <EquityCurve result={result} locale={locale} />
        <p className="shared-panel-note">{sharedResultText(locale, "equityDescription")}</p>
      </section>

      <section className="shared-report-columns">
        <article className="shared-report-panel shared-risk-panel">
          <p>{t("riskNoticeTitle")}</p>
          <h2>{t("riskNoticeBacktestTitle")}</h2>
          <p>{result.riskNote}</p>
        </article>
        <article className="shared-report-panel">
          <p>{t("methodology")}</p>
          <h2>{t("methodology")}</h2>
          <p>{sharedResultText(locale, "methodologyBody")}</p>
          <a className="shared-inline-link" href={result.methodologyURL} rel="noreferrer">{t("methodology")} →</a>
        </article>
      </section>

      <section className="shared-app-cta" aria-labelledby="shared-app-title">
        <div>
          <p>Backtest Trading</p>
          <h2 id="shared-app-title">{sharedResultText(locale, "openInApp")}</h2>
          <p>{sharedResultText(locale, "openHint")}</p>
        </div>
        <div className="shared-app-actions">
          {networkActions.duplicate && (
            <button
              className="shared-primary-action"
              type="button"
              disabled={duplicateBusy}
              onClick={onDuplicate}
            >
              {sharedResultText(locale, "duplicateStrategy")}
            </button>
          )}
          <a className="shared-primary-action" href={APP_STORE_URL} target="_blank" rel="noreferrer">{sharedResultText(locale, "viewOnAppStore")}</a>
        </div>
        {networkActions.duplicate && <p className="shared-duplicate-hint">{sharedResultText(locale, "duplicateHint")}</p>}
        {networkActions.duplicate && duplicateError && <p className="shared-action-error" role="alert">{sharedResultText(locale, "duplicateFailed")}</p>}
      </section>

      <aside className="shared-link-access-note">{sharedResultText(locale, "linkAccessNote")}</aside>
      <section className="shared-moderation-actions" aria-label={sharedResultText(locale, "reportResult")}>
        {networkActions.report && <button type="button" onClick={onReport}>{sharedResultText(locale, "reportResult")}</button>}
        <button type="button" onClick={onBlock}>{sharedResultText(locale, "blockResult")}</button>
        <a href="mailto:dattn2101@gmail.com">{sharedResultText(locale, "moderationContact")}: dattn2101@gmail.com</a>
      </section>
      <nav className="shared-fallback-links" aria-label="Backtest">
        <a href={`https://backtest.click${prefix}/`}>{sharedResultText(locale, "homepage")}</a>
        <a href={`https://backtest.click${prefix}/backtest`}>{sharedResultText(locale, "newBacktest")}</a>
      </nav>
    </main>
  );
}

function BlockedState({ locale }: { locale: SharedResultLocale }) {
  const prefix = locale === "en" ? "" : `/${locale}`;
  return (
    <main className="shared-state-card" id="main-content">
      <div className="shared-state-symbol is-not_found" aria-hidden="true">×</div>
      <h1>{sharedResultText(locale, "blockedTitle")}</h1>
      <p>{sharedResultText(locale, "blockedBody")}</p>
      <div className="shared-state-actions">
        <a href={`https://backtest.click${prefix}/backtest`}>{sharedResultText(locale, "newBacktest")}</a>
      </div>
    </main>
  );
}

function ReportDialog({
  locale,
  busy,
  received,
  acceptedWithoutDetails,
  supportContact,
  supportHref,
  failed,
  onClose,
  onSubmit,
}: {
  locale: SharedResultLocale;
  busy: boolean;
  received: boolean;
  acceptedWithoutDetails: boolean;
  supportContact: string;
  supportHref: string;
  failed: boolean;
  onClose(): void;
  onSubmit(reason: "misleading" | "spam" | "unsafe" | "other", details: string): void;
}) {
  const [reason, setReason] = useState<"misleading" | "spam" | "unsafe" | "other">("misleading");
  const [details, setDetails] = useState("");
  return (
    <div className="shared-dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) onClose();
    }}>
      <section className="shared-dialog" role="dialog" aria-modal="true" aria-labelledby="shared-report-dialog-title">
        <h2 id="shared-report-dialog-title">{sharedResultText(locale, "reportResult")}</h2>
        {received ? (
          acceptedWithoutDetails ? (
            <div className="shared-action-warning" role="alert">
              <p>{sharedResultText(locale, "reportDetailsNotRetained")}</p>
              <a href={supportHref}>
                {sharedResultText(locale, "moderationContact")}: {supportContact}
              </a>
            </div>
          ) : <p role="status">{sharedResultText(locale, "reportReceived")}</p>
        ) : (
          <>
            <label>
              <span>{sharedResultText(locale, "reportReason")}</span>
              <select value={reason} onChange={(event) => setReason(event.target.value as typeof reason)}>
                <option value="misleading">{sharedResultText(locale, "reportReasonMisleading")}</option>
                <option value="spam">{sharedResultText(locale, "reportReasonSpam")}</option>
                <option value="unsafe">{sharedResultText(locale, "reportReasonUnsafe")}</option>
                <option value="other">{sharedResultText(locale, "reportReasonOther")}</option>
              </select>
            </label>
            <label>
              <span>{sharedResultText(locale, "reportDetails")}</span>
              <textarea maxLength={500} value={details} onChange={(event) => setDetails(event.target.value)} />
            </label>
          </>
        )}
        {!received && failed && (
          <div className="shared-action-error" role="alert">
            <p>{sharedResultText(locale, "reportFailed")}</p>
            <a href="mailto:dattn2101@gmail.com?subject=Shared%20result%20report">
              {sharedResultText(locale, "moderationContact")}: dattn2101@gmail.com
            </a>
          </div>
        )}
        <div className="shared-dialog-actions">
          <button type="button" disabled={busy} onClick={onClose}>{sharedResultText(locale, "cancel")}</button>
          {!received && (
            <button type="button" disabled={busy} onClick={() => onSubmit(reason, details)}>
              {sharedResultText(locale, "reportSubmit")}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

export function SharedResultPage({ token }: { token: string }) {
  const [locale, setLocale] = useState<SharedResultLocale>(initialLocale);
  const [state, setState] = useState<PageState>(() => (
    bootstrapState() || (token ? { kind: "loading" } : { kind: "not_found" })
  ));
  const readyResult = state.kind === "ready" ? state.result : undefined;
  const [isBlocked, setIsBlocked] = useState(false);
  const [duplicateBusy, setDuplicateBusy] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);
  const duplicateAbortController = useRef<AbortController | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportReceived, setReportReceived] = useState(false);
  const [reportError, setReportError] = useState(false);
  const [reportReceipt, setReportReceipt] = useState<SharedResultReportReceipt | null>(null);

  useEffect(() => {
    protectBearerURL(locale, token, readyResult);
  }, [locale, readyResult, token]);

  useEffect(() => () => {
    duplicateAbortController.current?.abort();
    duplicateAbortController.current = null;
  }, []);

  useEffect(() => {
    let active = true;
    void sharedResultIsBlocked(token).then((blocked) => {
      if (active) setIsBlocked(blocked);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (state.kind !== "loading") return undefined;
    const controller = new AbortController();
    void fetchSharedResult(token, controller.signal).then((nextState) => {
      if (!controller.signal.aborted) setState(nextState);
    }).catch((error: unknown) => {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setState({ kind: "service_unavailable" });
      }
    });
    return () => controller.abort();
  }, [state.kind, token]);

  function selectLocale(nextLocale: SharedResultLocale) {
    writeStoredSharedResultLocale(() => window.localStorage, nextLocale);
    setLocale(nextLocale);
  }

  async function duplicateStrategy() {
    if (!readyResult?.shareID || !sharedResearchActionAvailability(readyResult).duplicate) return;
    duplicateAbortController.current?.abort();
    const controller = new AbortController();
    duplicateAbortController.current = controller;
    setDuplicateBusy(true);
    setDuplicateError(false);
    try {
      const destination = await sharedStrategyDestinationWithBestEffortAttribution(
        token,
        readyResult.shareID,
        locale,
        undefined,
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      window.location.assign(destination);
    } catch {
      if (!controller.signal.aborted) setDuplicateError(true);
    } finally {
      if (duplicateAbortController.current === controller) {
        duplicateAbortController.current = null;
        if (!controller.signal.aborted) setDuplicateBusy(false);
      }
    }
  }

  async function submitReport(
    reason: "misleading" | "spam" | "unsafe" | "other",
    details: string,
  ) {
    if (!readyResult || !sharedResearchActionAvailability(readyResult).report) {
      setReportOpen(false);
      return;
    }
    setReportBusy(true);
    setReportError(false);
    try {
      const receipt = await reportSharedResult(token, reason, details);
      setReportReceipt(receipt);
      setReportReceived(true);
    } catch {
      // Keep the dialog open so the user can retry without losing details.
      setReportError(true);
    } finally {
      setReportBusy(false);
    }
  }

  async function blockResult() {
    duplicateAbortController.current?.abort();
    duplicateAbortController.current = null;
    await blockSharedResult(token).catch(() => undefined);
    setIsBlocked(true);
  }

  const reportAcceptedWithoutDetails = reportReceipt?.detailsRetained === false;
  const reportSupport = sharedResultReportSupportLink(
    reportReceipt?.supportContact,
    readyResult?.shareID,
    reportAcceptedWithoutDetails,
  );

  return (
    <div className="shared-result-shell global-language-page">
      <LanguageSwitcher locale={locale} onChange={selectLocale} />
      <a className="shared-skip-link" href="#main-content">{sharedResultText(locale, "sharedTitle")}</a>
      <header className="shared-result-header">
        <a className="shared-result-brand" href="https://backtest.click/" aria-label="Backtest Trading">
          <img src="/backtest-icon.png?v=20260706" width="42" height="42" alt="" />
          <span><strong>Backtest Trading</strong><small>{sharedResultText(locale, "readOnly")}</small></span>
        </a>
      </header>
      {isBlocked
        ? <BlockedState locale={locale} />
        : state.kind === "loading"
        ? <LoadingState locale={locale} />
        : state.kind === "ready"
          ? (
              <EvidenceReport
                result={state.result}
                locale={locale}
                duplicateBusy={duplicateBusy}
                duplicateError={duplicateError}
                onDuplicate={() => void duplicateStrategy()}
                onReport={() => {
                  setReportReceived(false);
                  setReportError(false);
                  setReportReceipt(null);
                  setReportOpen(true);
                }}
                onBlock={() => void blockResult()}
              />
            )
          : <EmptyState state={state} locale={locale} />}
      {reportOpen && readyResult && sharedResearchActionAvailability(readyResult).report && (
        <ReportDialog
          locale={locale}
          busy={reportBusy}
          received={reportReceived}
          acceptedWithoutDetails={reportAcceptedWithoutDetails}
          supportContact={reportSupport.contact}
          supportHref={reportSupport.href}
          failed={reportError}
          onClose={() => {
            if (!reportBusy) setReportOpen(false);
          }}
          onSubmit={(reason, details) => void submitReport(reason, details)}
        />
      )}
      <footer className="shared-result-footer">
        <span>© {new Date().getFullYear()} Backtest Trading</span>
        <span>{researchT(locale, "riskNote")}</span>
      </footer>
    </div>
  );
}

export const sharedResultPageTesting = {
  bootstrapState,
  fetchSharedResult,
  evidenceLabel,
};
